import axios from "../../utils/axios.tsx";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import TallyButton from "../common/TallyButton.tsx";
import ProgressMessage from "./ProgressMessage.tsx";
import Link from "@mui/material/Link";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { useDispatch, useSelector } from "react-redux";
import { checkEconomicIntegration } from "../../store/economic";
import { RootState, AppDispatch } from "../../store/types";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import { useSSE } from "../../hooks";
import { useLocalizedSyncMessage } from "../../hooks/useLocalizedSyncMessage";
import { BASE_URL } from "../../const.ts";
import type {
  SyncStatus,
  SyncIntegrationResponse,
  ApiResponse,
  SSEMessage,
  SSEOptions,
} from "../../types";
import { streamAsk } from "../search/streamAsk.tsx";

interface SyncStatusProps {
  children?: React.ReactNode;
  content: SyncStatus | string;
  role: string;
  onPulseAnimation?: () => void;
  stopPulseAnimation?: () => void;
}

// Utility function to convert UTC to local time
const formatDate = (utcString: string) => {
  if (!utcString) return "N/A";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(utcString));
};

// Component for rendering individual sync messages
const SyncMessageItem: React.FC<{
  message: string | Record<string, any>;
  index: number;
  isLast: boolean;
}> = ({ message, index, isLast }) => {
  let displayMessage: string;

  // Check if message is a LocalizedMessage object
  if (typeof message === "object" && message !== null && "key" in message) {
    // It's a LocalizedMessage object - use the hook
    const localizedMsg = useLocalizedSyncMessage(message as any);
    displayMessage = (localizedMsg || message.key || "") as string;
  } else if (typeof message === "string") {
    // It's a plain string
    displayMessage = message;
  } else {
    displayMessage = String(message);
  }

  return (
    <ProgressMessage
      progressUpdate={isLast}
      sx={{ marginBottom: "8px", display: "block" }}
    >
      {index > 0 && "✓ "}
      {displayMessage}
    </ProgressMessage>
  );
};

function syncIntegration(
  integration_identifier: string = "economic",
): Promise<ApiResponse<SyncIntegrationResponse>> {
  // Call /integrations/sync/ with the integration identifer
  // The identifier is hardcoded to economic for now
  // Use axios to make the request
  // Sleep and fake sync call (handy for development):
  // return new Promise((resolve) => {
  //   setTimeout(() => {
  //     resolve();
  //   }, 3000);
  // });

  return axios.post(
    "/v1/integrations/sync/",
    JSON.stringify({ integration_identifier: integration_identifier }),
    { withCredentials: true },
  );
}

const SyncStatus: React.FC<SyncStatusProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasIntegration: hasEconomicIntegration, isLoading } = useSelector(
    (state: RootState) => state.economic,
  );
  const [awaitingSyncResponse, setAwaitingSyncResponse] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(() => {
    if (typeof props.content === "string") {
      try {
        return JSON.parse(props.content) as SyncStatus;
      } catch (error) {
        console.error("Failed to parse sync status content:", error);
        // Return a default sync status
        return {
          title_message: i18n._(
            msg({
              context: "Integration status title",
              message: "Integration Status",
            }),
          ),
          message: "Failed to parse sync status",
          sync_id: null,
          last_sync_start: null,
          last_sync_end: null,
          last_successful_sync_start: null,
          last_successful_sync_end: null,
          last_error_traceback: "",
          integration: "economic",
          error: "Parse error",
        };
      }
    }
    return props.content;
  });
  const [syncMessages, setSyncMessages] = useState<
    (string | Record<string, any>)[]
  >([]);
  const [sseUrl, setSseUrl] = useState<string | null>(null);
  const isAuthenticated = useSelector(
    (state: RootState) => state.authState.isLoggedIn,
  );
  const selectedModel = useSelector(
    (state: RootState) => state.conversationState.selectedModel,
  );
  const [hasTriggeredCustomGoal, setHasTriggeredCustomGoal] =
    React.useState(false);

  useEffect(() => {
    if (isLoading) {
      dispatch(checkEconomicIntegration());
    }
  }, [dispatch, isLoading]);

  // Memoize SSE options to prevent unnecessary EventSource recreation
  const sseOptions = useMemo<SSEOptions>(
    () => ({
      onMessage: (data: SSEMessage) => {
        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] SyncStatus received SSE message:`, data);
        if (data.type === "status") {
          setSyncStatus(data.data);
          // Add the new message to our message history
          // Support both old (message) and new (body) formats
          // Store the full body object for proper parameter interpolation
          const messageText = data.data.message || data.data.body;
          if (messageText) {
            setSyncMessages((prev) => [...prev, messageText]);
          }
        } else if (data.type === "completed") {
          console.log(`[${timestamp}] Sync completed - closing SSE connection`);
          setSseUrl(null);
          setAwaitingSyncResponse(false);
          if (props.stopPulseAnimation) {
            props.stopPulseAnimation();
          }
          setSyncMessages([]);
        } else if (data.type === "stream_ended") {
          console.log(`[${timestamp}] Stream ended - closing SSE connection`);
          setSseUrl(null);
          setAwaitingSyncResponse(false);
          if (props.stopPulseAnimation) {
            props.stopPulseAnimation();
          }
          setSyncMessages([]);
        } else if (data.type === "error") {
          console.error(`[${timestamp}] SSE error:`, data.message);
          setSseUrl(null);
          setAwaitingSyncResponse(false);
          if (props.stopPulseAnimation) {
            props.stopPulseAnimation();
          }
          setSyncMessages([]);
        } else {
          console.log(`[${timestamp}] Unknown message type:`, data.type);
        }
      },
      onError: (error: Event) => {
        console.error("SSE connection error:", error);
        setSseUrl(null);
        setAwaitingSyncResponse(false);
        if (props.stopPulseAnimation) {
          props.stopPulseAnimation();
        }
        // Clear message history on connection error
        setSyncMessages([]);
      },
      onOpen: () => {
        console.log("SSE connection opened");
      },
      onClose: () => {
        console.log("SSE connection closed");
      },
    }),
    [props],
  );
  // SSE connection
  const { closeConnection } = useSSE(sseUrl, sseOptions);

  const syncNow = useCallback(async () => {
    setAwaitingSyncResponse(true);
    // Clear any previous sync messages when starting new sync
    setSyncMessages([]);
    if (props.onPulseAnimation) {
      props.onPulseAnimation();
    }

    try {
      // Start sync
      const syncResponse = await syncIntegration("economic");

      // Extract signed value and sync_id from response
      const signedValue = syncResponse.data.signed_value;
      const syncId = syncResponse.data.sync_id;

      if (!signedValue) {
        console.error("No signed value received from sync response");
        setAwaitingSyncResponse(false);
        if (props.stopPulseAnimation) {
          props.stopPulseAnimation();
        }
        return;
      }

      if (!syncId) {
        console.error("No sync_id received from sync response");
        setAwaitingSyncResponse(false);
        if (props.stopPulseAnimation) {
          props.stopPulseAnimation();
        }
        return;
      }

      // Start SSE connection with signed authentication and sync_id
      setSseUrl(
        `${BASE_URL}/v1/integrations/sync/stream/economic/?user_signature=${encodeURIComponent(signedValue)}&sync_id=${encodeURIComponent(syncId)}`,
      );
    } catch (error) {
      console.error("Error syncing integration:", error);
      setAwaitingSyncResponse(false);
      if (props.stopPulseAnimation) {
        props.stopPulseAnimation();
      }
    }
  }, [props]);

  // Check if we should start the first sync after connecting
  useEffect(() => {
    const shouldStartFirstSync = localStorage.getItem("shouldStartFirstSync");
    if (
      shouldStartFirstSync === "true" &&
      hasEconomicIntegration &&
      !awaitingSyncResponse
    ) {
      // Clear the flag
      localStorage.removeItem("shouldStartFirstSync");
      // Start the first sync automatically
      syncNow();
    }
  }, [hasEconomicIntegration, awaitingSyncResponse, syncNow]);

  // Trigger custom goal after successful sync
  useEffect(() => {
    const triggerCustomGoal = async () => {
      // After the first successful sync for a new user, trigger a flow
      // where tally introduces more about the platform to the user.
      if (syncStatus.last_successful_sync_end && !hasTriggeredCustomGoal) {
        const welcomeConversationId = localStorage.getItem(
          "welcome_conversation_identifier",
        );
        const isNewUser = !!welcomeConversationId;

        if (isNewUser) {
          setHasTriggeredCustomGoal(true);

          const stopAnimation = () => {};

          const exchangeParams = {
            conversation_id: welcomeConversationId,
            prompt: "",
            custom_goal_identifier: "post_first_sync_welcome",
            model: selectedModel,
          };
          await streamAsk({
            dispatch,
            isAuthenticated,
            stopAnimation,
            exchangeURL: "/v1/exchange/",
            streamSlug: "/v1/stream_ask/",
            exchangeParams: exchangeParams,
          });
          if (welcomeConversationId) {
            localStorage.removeItem("welcome_conversation_identifier");
          }
        }
      }
    };

    triggerCustomGoal();
  }, [
    syncStatus,
    hasTriggeredCustomGoal,
    dispatch,
    isAuthenticated,
    selectedModel,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closeConnection();
    };
  }, [closeConnection]);

  return (
    <Box marginBottom={"4%"}>
      <Grid container>
        <Grid size={{ xs: 1, md: 1, lg: 1 }}>
          <AvatarPanel />
        </Grid>
        <Grid size={{ xs: 12, md: 10, lg: 10 }}>
          <Box sx={{ marginBottom: "3%" }}>
            <CustomReactMarkDown
              content={
                i18n._({ id: "integration.status.title" }) ||
                "Integration Status"
              }
            />
          </Box>
          {hasEconomicIntegration && (
            <>
              {awaitingSyncResponse ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "flex-start",
                    minHeight: "150px",
                    marginLeft: "20%",
                    paddingTop: "20px",
                  }}
                >
                  {syncMessages.length > 0 ? (
                    <Box sx={{ textAlign: "left", maxWidth: "100%" }}>
                      {syncMessages.map((message, index) => (
                        <SyncMessageItem
                          key={index}
                          message={message}
                          index={index}
                          isLast={index === syncMessages.length - 1}
                        />
                      ))}
                    </Box>
                  ) : (
                    <CircularProgress />
                  )}
                </Box>
              ) : (
                <>
                  {syncStatus.last_error_traceback ? (
                    <>
                      <Typography variant="body1" color="black">
                        {`${i18n._(
                          msg({
                            context:
                              "Response title which is followed by the last sync status info",
                            message: "Last sync:",
                          }),
                        )} ${formatDate(syncStatus.last_sync_end || "")}`}
                      </Typography>
                      <Typography variant="body1" color="black">
                        {`${i18n._(
                          msg({
                            context:
                              "Response title which is followed by the error while trying to fetch the last sync status info",
                            message: "Error:",
                          }),
                        )} ${syncStatus.last_error_traceback}`}
                        <br />
                        <Trans
                          context={
                            "Explanation of the next steps taken after there is an error with sync."
                          }
                        >
                          An engineer will look into this within the next 4
                          hours within working hours.
                        </Trans>
                      </Typography>
                      <Typography variant="body1" color="black">
                        {`${i18n._(
                          msg({
                            context:
                              "Response title which is followed by the last successful sync status info",
                            message: "Last successful sync:",
                          }),
                        )} ${formatDate(syncStatus.last_successful_sync_end || "")}`}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" color="black">
                        {`${i18n._(
                          msg({
                            context:
                              "Response title which is followed by the last successful sync status info",
                            message: "Last successful sync:",
                          }),
                        )} ${formatDate(syncStatus.last_successful_sync_end || "")}`}
                      </Typography>
                      {syncStatus.additional_information ? (
                        <Typography variant="body1" color="black">
                          <CustomReactMarkDown
                            content={syncStatus.additional_information}
                          ></CustomReactMarkDown>
                        </Typography>
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </>
              )}
              {!awaitingSyncResponse && (
                <TallyButton
                  sx={{
                    marginBottom: "2%",
                    marginTop: "2%",
                  }}
                  onClick={syncNow}
                >
                  <Trans
                    context={
                      "Button title enabling the user to sync the status."
                    }
                  >
                    Sync Now
                  </Trans>
                </TallyButton>
              )}
            </>
          )}
          {!isLoading && !hasEconomicIntegration && (
            <Link
              href="https://secure.e-conomic.com/secure/api1/requestaccess.aspx?appPublicToken=2W6NhwKKvweaPWY19vxXs0mn1GtiVk49xIYOq3R2z2o&redirectUrl=https%3A%2F%2Fasktally.com%2Fintegration%2Fe-conomic%2Fconnect"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              <Typography variant="body2">
                <Trans
                  context={
                    "Button title enabling the user to connect to 'e-conomic' service."
                  }
                >
                  Click here to connect to e-conomic
                </Trans>
              </Typography>
            </Link>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SyncStatus;
